//import Vue from 'vue';
import utils from './utils.jsx';
import careHelpfulFunctions from '../Application/careHelpfulFunctions.jsx';

// Wrapper for a compiled expression. Allows recognition of the compiled expression and
// also allows us to store the original string (for debugging).
class ObjFunc {
    constructor(code, func) {
        this.code = code;
        if (!func)
            this.func = new Function('context', 'util', 'with (context) return ' + code + ';');
        else
            this.func = func;
    }
}

// Allows objects to be added to an ObjProxy without them getting read through a proxy themselves
class PlainWrapper {
    constructor(obj) {
        this.obj = () => obj;
    }
}

// This is a proxy wrapper for an object with interpolated expressions within its children.
// Allows the user control to defer resolution of the property values to the child components
// that want to read them. Prevents the user control from being refreshed when any of the
// dependent values change and allows child components to refresh instead.
class ObjProxy {
    constructor(target) {
        this.target = () => target;
        this.handler = {
            //context: null, // Set by the evaluateObject method
            get: function (oTarget, oKey, receiver) {
                const item = Reflect.get(oTarget, oKey, receiver);

                if (item instanceof ObjFunc)
                    return item.func(this.context, careHelpfulFunctions);
                else if (item === undefined || item === null)
                    return item;
                else if (item instanceof PlainWrapper)
                    return item.obj();
                else if (typeof item === 'object')
                    return new Proxy(item, this);
                else
                    return item;
            },
            set: function (oTarget, oKey, oValue, receiver) {
                return Reflect.set(oTarget, oKey, oValue, receiver);
            },
            has: function (oTarget, oKey) {
                return Reflect.has(oTarget, oKey);
            },
        };
        this.myproxy = () => new Proxy(target, this.handler);
    }
}

// UserControl keeps libraries on newscopeitems; descendants receive that bag as scopeitems.
function resolveScopeLibraries(oTarget) {
    if ('scopeitems' in oTarget) {
        let libs = utils.getScopeLibraries(oTarget.scopeitems);
        if (libs)
            return libs;
    }

    if ('$options' in oTarget && 'newscopeitems' in oTarget) {
        const name = oTarget.$options?.name;
        if (name === 'dynamic-user-control' || name === 'DynamicUserControlHsm')
            return utils.getScopeLibraries(oTarget.newscopeitems);
    }
    return null;
}

// This is a wrapper for the context object that we are passing into dynamic functions that
// expect the context to usually be the Vue component instance. To invoke dynamic functions
// we have to enclose them in a 'with' statement to allow things like VarByName, ParamByName,
// etc. to be invoked without an object prefix. The Vue 3 instance is a proxy and if you
// try and pass it to a 'with' statement, it attempts to read things from the proxy that
// aren't valid from Vue's perspective and it generates a noisy and verbose warning (with
// stack trace). To avoid this penalty, we have our own proxy that checks to be sure the
// bad reads are ignored before passing them on to the Vue instance.
class WithProxy {
    constructor(target) {
        this.handler = {
            get: function (oTarget, oKey, receiver) {
                if (typeof oKey === 'string') {
                    if (oKey in oTarget)
                        return Reflect.get(oTarget, oKey, receiver);

                    let libs = resolveScopeLibraries(oTarget);
                    if (libs && oKey in libs)
                        return libs[oKey];

                    return undefined;
                }
                return undefined;
            },
            set: function (oTarget, oKey, oValue, receiver) {
                return Reflect.set(oTarget, oKey, oValue, receiver);
            },
            has: function (oTarget, oKey) {
                if (Reflect.has(oTarget, oKey))
                    return true;

                let libs = resolveScopeLibraries(oTarget);
                if (libs && typeof oKey === 'string' && oKey in libs)
                    return true;

                return false;
            },
        };
        this.proxy = new Proxy(target, this.handler);
    }
}

export {
    PlainWrapper,
    ObjProxy,
    ObjFunc,
    WithProxy,
};